import { useEffect } from "react";
import { setHideSidebarExpandIcon, setSidebarExpanded } from "@/modules/app/appSlice";
import useAppContext from "@/modules/app/useAppContext";

interface UseSidebarExpansionOptions {
  /**
   * Whether to expand the sidebar when the component mounts
   * @default true
   */
  expandOnMount?: boolean;
  /**
   * Whether to collapse the sidebar when the component unmounts
   * @default true
   */
  collapseOnUnmount?: boolean;
  /**
   * Whether to hide the sidebar expand icon when the component mounts
   * @default false
   */
  hideExpandToggleIconOnMount?: boolean;
}

/**
 * Custom hook to manage sidebar expansion state for specific pages/components.
 * 
 * This hook automatically handles expanding/collapsing the sidebar when a component
 * mounts/unmounts, which is commonly needed for pages that require different sidebar
 * states (e.g., detail pages that need collapsed sidebar, list pages that need expanded).
 * 
 * @param options Configuration options for sidebar behavior
 * @returns void
 * 
 * @example
 * // Expand sidebar on mount, collapse on unmount (default behavior)
 * useSidebarExpansion();
 * 
 * @example
 * // Collapse sidebar on mount, expand on unmount
 * useSidebarExpansion({ expandOnMount: false, collapseOnUnmount: false });
 * 
 * @example
 * // Only expand on mount, don't change on unmount
 * useSidebarExpansion({ collapseOnUnmount: false });
 */
export const useSidebarExpansion = (options: UseSidebarExpansionOptions = {}) => {
  const { expandOnMount = true, collapseOnUnmount = true, hideExpandToggleIconOnMount = false } = options;
  const { dispatch } = useAppContext();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(setSidebarExpanded(expandOnMount));
      dispatch(setHideSidebarExpandIcon(hideExpandToggleIconOnMount));
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      if (collapseOnUnmount) {
        dispatch(setSidebarExpanded(!expandOnMount));
      }
      // always show the expand icon when leaving the page
      dispatch(setHideSidebarExpandIcon(false))
    };
  }, [dispatch, expandOnMount, collapseOnUnmount, hideExpandToggleIconOnMount]);
};
